import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MessageSquare, Paperclip } from 'lucide-react';

const TYPE_STYLES = {
  bug: 'bg-red-100 text-red-700',
  feature: 'bg-purple-100 text-purple-700',
  improvement: 'bg-blue-100 text-blue-700',
};

const TYPE_LABELS = { bug: 'Bug', feature: 'Feature', improvement: 'Improvement' };

const PRIORITY_DOT = {
  critical: 'bg-red-500 animate-pulse',
  high: 'bg-orange-400',
  medium: 'bg-yellow-400',
  low: 'bg-gray-300',
};

const IssueCard = ({ issue, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: issue.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const initials = issue.assignee_name
    ? issue.assignee_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-3.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer select-none"
    >
      {/* Title */}
      <p className="text-sm font-medium text-gray-800 leading-snug mb-2.5 line-clamp-2">{issue.title}</p>

      {/* Badges row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_STYLES[issue.issue_type] || 'bg-gray-100 text-gray-600'}`}>
          {TYPE_LABELS[issue.issue_type] || issue.issue_type}
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[issue.priority] || 'bg-gray-300'}`} />
          <span className="capitalize">{issue.priority}</span>
        </span>
        {issue.story_points != null && (
          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-mono">
            {issue.story_points}pt
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-400">
          {issue.comment_count > 0 && (
            <span className="flex items-center gap-0.5 text-xs">
              <MessageSquare className="h-3 w-3" />
              {issue.comment_count}
            </span>
          )}
          {issue.attachment_count > 0 && (
            <span className="flex items-center gap-0.5 text-xs">
              <Paperclip className="h-3 w-3" />
              {issue.attachment_count}
            </span>
          )}
        </div>
        {initials && (
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center" title={issue.assignee_name}>
            <span className="text-white text-xs font-bold">{initials}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueCard;
